
interface CurrencyConfig {
  icon: string;
  locale: string;
  decimalPlaces: number;
}

export const BDT_TO_AUD_RATE = 92;

type CurrencyCode = "USD" | "BDT" | "AUD";
// Currency configuration
const currencyConfig: Record<CurrencyCode, CurrencyConfig> = {
  USD: {
    icon: "$",
    locale: "en-US",
    decimalPlaces: 2,
  },
  BDT: {
    icon: "৳",
    locale: "bn-BD",
    decimalPlaces: 2,
  },
  AUD: {
    icon: "AUD ",
    locale: "en-AU",
    decimalPlaces: 2,
  },
};

/**
 * Converts BDT values from the API to AUD for display.
 */
export function convertBdtToAud(
  price: number | string | null | undefined,
): number {
  if (price === undefined || price === null) {
    return 0;
  }

  const numericPrice = typeof price === "string" ? parseFloat(price) : price;
  if (!Number.isFinite(numericPrice)) {
    return 0;
  }

  return numericPrice / BDT_TO_AUD_RATE;
}

/**
 * Formats a BDT API price as AUD for frontend display
 * @param price - The price to format
 * @param currency - The source currency code. Existing frontend prices are BDT.
 * @returns Formatted price with currency icon or '0' if price is null/undefined
 */

export function priceFormatter(
  price: number | string | null | undefined,
  currency: CurrencyCode = "BDT",
  icon: boolean = true
): string {
  // Handle undefined or null price
  if (price === undefined || price === null) {
    return icon ? "AUD 0.00" : "0.00";
  }

  // Convert price to number if it's a string
  const numericPrice: number =
    typeof price === "string" ? parseFloat(price) : price;

  // Validate the price
  if (isNaN(numericPrice)) {
    return "Invalid price";
  }

  const displayPrice = currency === "AUD" ? numericPrice : numericPrice / BDT_TO_AUD_RATE;
  const config = currencyConfig.AUD;

  try {
    const formatter = new Intl.NumberFormat(config.locale, {
      style: "decimal",
      minimumFractionDigits: config.decimalPlaces,
      maximumFractionDigits: config.decimalPlaces,
      numberingSystem: "latn", // Ensure standard digits
    });

    const formattedPrice = formatter.format(displayPrice);
    // If icon is true, prepend the currency icon
    if (icon) {
      return `${config.icon}${formattedPrice}`;
    }
    return formattedPrice;
  } catch (_error) {
    const fallbackPrice = displayPrice.toFixed(config.decimalPlaces);
    return `${config.icon}${fallbackPrice}`;
  }
}

